import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from './ui/table';
import { X, Eye } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import { 
  CheckCircle, XCircle, Clock, RefreshCw, Play, FileText, 
  User, MessageSquare, ChevronDown, ChevronUp, Save, BarChart3,
  AlertTriangle, CheckCheck, Edit3
} from 'lucide-react';
import { toast } from 'sonner';
import BatchHistory from './BatchHistory';
import AgentNotes from './AgentNotes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// HubSpot Portal ID (de la config de integración)
// NOTA: Si cambia el portal, actualizar también en backend derivaciones_routes.py
// Portal ID de Capabilia (confirmado por Mariano)
// Configurable via variable de entorno REACT_APP_HUBSPOT_PORTAL_ID
const HUBSPOT_PORTAL_ID = process.env.REACT_APP_HUBSPOT_PORTAL_ID || '3877362';

// Componente para link a ticket de HubSpot
// Formato correcto: https://app.hubspot.com/contacts/{PORTAL_ID}/record/0-5/{ticketId}
// 0-5 = object type ID para tickets en HubSpot
export const TicketLink = ({ ticketId, className = '' }) => {
  if (!ticketId || ticketId === 'unknown') {
    return <span className={`font-mono text-sm ${className}`}>{ticketId || 'N/A'}</span>;
  }
  
  // Formato correcto de HubSpot para tickets (record/0-5)
  const hubspotUrl = `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/record/0-5/${ticketId}`;
  
  return (
    <a 
      href={hubspotUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline ${className}`}
      title="Abrir ticket en HubSpot"
    >
      {ticketId}
    </a>
  );
};

// Banner de piloto sin validar - visible y permanente en TODAS las vistas del módulo
// (scores, ranking, batches, detalle). Los números vienen en vivo de metrics/stats
// (props), nunca hardcodeados. El gate se relaja solo cuando SE CUMPLEN LAS DOS
// condiciones: agreement >= 80% Y al menos MIN_CALIBRATED_SAMPLE casos calibrados
// (con n chico, un 80% no es representativo - piso acordado con negocio).
export const PILOT_AGREEMENT_THRESHOLD = 80;
export const MIN_CALIBRATED_SAMPLE = 20;

export const isPilotLocked = (agreementRate = 0, totalCalibrated = 0) =>
  agreementRate < PILOT_AGREEMENT_THRESHOLD || totalCalibrated < MIN_CALIBRATED_SAMPLE;

export const PilotBanner = ({ totalCalibrated = 0, total = 0, agreementRate = 0 }) => {
  const missingCases = Math.max(0, MIN_CALIBRATED_SAMPLE - totalCalibrated);
  const missingAgreement = agreementRate < PILOT_AGREEMENT_THRESHOLD;
  const locked = isPilotLocked(agreementRate, totalCalibrated);
  return (
    <div
      className={`rounded-lg border-2 p-3 flex items-start gap-3 ${
        locked ? 'bg-amber-50 border-amber-400' : 'bg-green-50 border-green-400'
      }`}
      data-testid="pilot-banner"
    >
      <AlertTriangle className={`h-5 w-5 mt-0.5 shrink-0 ${locked ? 'text-amber-600' : 'text-green-600'}`} />
      <div className="text-sm leading-snug">
        {locked ? (
          <>
            <span className="font-bold text-amber-800">⚠️ PILOTO — IA sin validar.</span>{' '}
            <span className="text-amber-800">
              Calibración <strong data-testid="pilot-banner-calibrated">{totalCalibrated}</strong>/
              <strong data-testid="pilot-banner-total">{total}</strong>, agreement{' '}
              <strong data-testid="pilot-banner-agreement">{agreementRate}</strong>%. Estas notas NO deben
              usarse para decisiones sobre agentes ni proveedores hasta alcanzar agreement ≥
              {PILOT_AGREEMENT_THRESHOLD}% con al menos {MIN_CALIBRATED_SAMPLE} casos calibrados.{' '}
              <span data-testid="pilot-banner-missing" className="italic">
                {missingCases > 0 && missingAgreement
                  ? `Faltan ≥${missingCases} casos calibrados y ≥${PILOT_AGREEMENT_THRESHOLD}% de coincidencia.`
                  : missingCases > 0
                  ? `Faltan ≥${missingCases} casos calibrados para llegar al mínimo de ${MIN_CALIBRATED_SAMPLE}.`
                  : `Falta subir el acuerdo a ≥${PILOT_AGREEMENT_THRESHOLD}% (hoy ${agreementRate}%).`}
              </span>
            </span>
          </>
        ) : (
          <>
            <span className="font-bold text-green-800">✅ Calibración validada.</span>{' '}
            <span className="text-green-800">
              Calibración <strong data-testid="pilot-banner-calibrated">{totalCalibrated}</strong>/
              <strong data-testid="pilot-banner-total">{total}</strong>, agreement{' '}
              <strong data-testid="pilot-banner-agreement">{agreementRate}</strong>% (≥{PILOT_AGREEMENT_THRESHOLD}%
              con ≥{MIN_CALIBRATED_SAMPLE} casos calibrados). Notas habilitadas para decisiones sobre
              agentes/proveedores.
            </span>
          </>
        )}
      </div>
    </div>
  );
};

// Badge "Provisorio" para que ninguna nota/ranking individual se lea como definitiva
// mientras el piloto no esté calibrado (misma fuente de verdad que el banner).
export const ProvisionalBadge = ({ agreementRate = 0, totalCalibrated = 0 }) => {
  if (!isPilotLocked(agreementRate, totalCalibrated)) return null;
  return (
    <Badge
      variant="outline"
      className="border-amber-400 text-amber-700 bg-amber-50 text-xs"
      data-testid="provisional-badge"
      title="Piloto sin calibrar - no usar para decisiones sobre agentes/proveedores"
    >
      Provisorio
    </Badge>
  );
};

// Nombres legibles de criterios
export const CRITERIO_LABELS = {
  tipificacion: { name: 'Tipificación', peso: '15%', icon: '🏷️' },
  calidad_respuesta: { name: 'Calidad de Respuesta', peso: '20%', icon: '✍️' },
  derivacion: { name: 'Derivación', peso: '10%', icon: '↗️' },
  resolucion: { name: 'Resolución', peso: '25%', icon: '✅' },
  tono: { name: 'Tono y Experiencia', peso: '15%', icon: '💬' },
  aviso_csat: { name: 'Aviso CSAT', peso: '15%', icon: '📊' }
};

// Componente de Score Badge con color
export const ScoreBadge = ({ score, size = 'default' }) => {
  let colorClass = 'bg-red-100 text-red-800 border-red-200';
  if (score >= 85) colorClass = 'bg-green-100 text-green-800 border-green-200';
  else if (score >= 70) colorClass = 'bg-yellow-100 text-yellow-800 border-yellow-200';
  else if (score >= 50) colorClass = 'bg-orange-100 text-orange-800 border-orange-200';
  
  const sizeClass = size === 'large' ? 'text-2xl px-4 py-2' : 'text-sm px-2 py-1';
  
  return (
    <span className={`inline-flex items-center rounded-md border font-semibold ${colorClass} ${sizeClass}`}>
      {score}
    </span>
  );
};

// Componente de mensaje en el hilo
const ConversationMessage = ({ message }) => {
  const isAgent = message.direction === 'OUTGOING';
  const timestamp = message.created_at ? 
    new Date(message.created_at).toLocaleString('es-AR', { 
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
    }) : '';
  
  return (
    <div className={`flex ${isAgent ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[80%] rounded-lg p-3 ${
        isAgent 
          ? 'bg-blue-50 border border-blue-200' 
          : 'bg-gray-50 border border-gray-200'
      }`}>
        <div className="flex items-center gap-2 mb-1">
          <Badge variant={isAgent ? 'default' : 'secondary'} className="text-xs">
            {isAgent ? 'AGENTE' : 'ALUMNO'}
          </Badge>
          <span className="text-xs text-gray-500">{timestamp}</span>
        </div>
        <p className="text-sm whitespace-pre-wrap">{message.text}</p>
      </div>
    </div>
  );
};

// Modal de detalle y calibración
const CalibrationDetailModal = ({ 
  ticketId, 
  isOpen, 
  onClose, 
  token,
  agreementRate = 0,
  totalCalibrated = 0,
  totalScores = 0,
  onCalibrated 
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [calibraciones, setCalibraciones] = useState({});
  const [notasGenerales, setNotasGenerales] = useState('');
  const [expandedCriterios, setExpandedCriterios] = useState({});
  
  // Cargar detalle
  useEffect(() => {
    if (!isOpen || !ticketId) return;
    
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/api/agent-quality/calibration/score/${ticketId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (!response.ok) throw new Error('Error cargando detalle');
        
        const result = await response.json();
        setData(result);
        
        // Inicializar calibraciones desde datos existentes
        const existingCal = result.score?.criterios_calibrados || {};
        setCalibraciones(existingCal);
        setNotasGenerales(result.score?.notas_calibracion || '');
        
      } catch (error) {
        toast.error('Error cargando detalle del ticket');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDetail();
  }, [isOpen, ticketId, token]);
  
  // Toggle expandir criterio
  const toggleCriterio = (codigo) => {
    setExpandedCriterios(prev => ({
      ...prev,
      [codigo]: !prev[codigo]
    }));
  };
  
  // Actualizar calibración de un criterio
  const updateCalibracion = (codigo, field, value) => {
    setCalibraciones(prev => ({
      ...prev,
      [codigo]: {
        ...prev[codigo],
        [field]: value
      }
    }));
  };
  
  // Guardar calibración
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/agent-quality/calibration/score/${ticketId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            criterios_calibrados: calibraciones,
            notas_calibracion: notasGenerales
          })
        }
      );
      
      if (!response.ok) throw new Error('Error guardando calibración');
      
      toast.success('Calibración guardada');
      onCalibrated?.();
      onClose();
      
    } catch (error) {
      toast.error('Error guardando calibración');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] flex flex-col p-0 bg-white">
        {/* Header FIJO */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0 bg-white">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Calibración - Ticket <TicketLink ticketId={ticketId} />
          </DialogTitle>
          <DialogDescription>
            Revisa el score del LLM y ajusta si es necesario
          </DialogDescription>
        </DialogHeader>
        
        <div className="px-6 pt-4 shrink-0">
          <PilotBanner totalCalibrated={totalCalibrated} total={totalScores} agreementRate={agreementRate} />
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-12 flex-1">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : data ? (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <Tabs defaultValue="criterios" className="flex-1 flex flex-col min-h-0">
              {/* Tabs FIJOS */}
              <div className="px-6 pt-4 shrink-0">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="criterios">Criterios & Calibración</TabsTrigger>
                  <TabsTrigger value="conversacion">Conversación</TabsTrigger>
                  <TabsTrigger value="ticket">Datos del Ticket</TabsTrigger>
                </TabsList>
              </div>
              
              {/* Tab Criterios - SCROLLABLE */}
              <TabsContent value="criterios" className="flex-1 overflow-y-auto px-6 pb-4 mt-4">
                <div className="space-y-4">
                  {/* Resumen de scores */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">Score LLM</p>
                      <div className="flex items-center gap-2">
                        <ScoreBadge score={data.score?.score_global || 0} size="large" />
                        <ProvisionalBadge agreementRate={agreementRate} totalCalibrated={totalCalibrated} />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Agente</p>
                      <p className="font-medium">{data.score?.agent_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Estado</p>
                      <Badge variant={data.score?.calibration_status === 'calibrated' ? 'default' : 'secondary'}>
                        {data.score?.calibration_status === 'calibrated' ? 'Calibrado' : 'Pendiente'}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Lista de criterios */}
                  <div className="space-y-3">
                    {data.score?.criterios?.map((criterio) => {
                      const label = CRITERIO_LABELS[criterio.codigo] || { name: criterio.criterio, peso: '', icon: '📋' };
                      const isExpanded = expandedCriterios[criterio.codigo];
                      const cal = calibraciones[criterio.codigo] || {};
                      
                      return (
                        <Card key={criterio.codigo} className="overflow-hidden">
                          <div 
                            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => toggleCriterio(criterio.codigo)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-xl">{label.icon}</span>
                                <div>
                                  <p className="font-medium">{label.name}</p>
                                  <p className="text-xs text-gray-500">Peso: {label.peso} | Método: {criterio.metodo}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-center">
                                  <p className="text-xs text-gray-500">LLM</p>
                                  <ScoreBadge score={criterio.score} />
                                </div>
                                {cal.score_humano !== undefined && (
                                  <div className="text-center">
                                    <p className="text-xs text-gray-500">Humano</p>
                                    <ScoreBadge score={cal.score_humano} />
                                  </div>
                                )}
                                {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                              </div>
                            </div>
                          </div>
                          
                          {isExpanded && (
                            <div className="px-4 pb-4 border-t bg-gray-50">
                              <div className="pt-4 space-y-4">
                                {/* Justificación LLM */}
                                <div>
                                  <p className="text-sm font-medium mb-1">Justificación LLM:</p>
                                  <p className="text-sm text-gray-600 bg-white p-2 rounded border">
                                    {criterio.justificacion || 'Sin justificación'}
                                  </p>
                                </div>
                                
                                {/* Detalles adicionales */}
                                {criterio.detalles && Object.keys(criterio.detalles).length > 0 && (
                                  <div>
                                    <p className="text-sm font-medium mb-1">Detalles:</p>
                                    <pre className="text-xs bg-white p-2 rounded border overflow-auto">
                                      {JSON.stringify(criterio.detalles, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                
                                {/* Calibración humana */}
                                <div className="border-t pt-4 mt-4">
                                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <Edit3 className="h-4 w-4" />
                                    Calibración Humana
                                  </p>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <label className="text-sm text-gray-500">Score corregido (0-100)</label>
                                      <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={cal.score_humano ?? ''}
                                        onChange={(e) => updateCalibracion(
                                          criterio.codigo, 
                                          'score_humano', 
                                          e.target.value ? parseInt(e.target.value) : null
                                        )}
                                        placeholder={`Original: ${criterio.score}`}
                                        className="mt-1"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-sm text-gray-500">Nota del revisor</label>
                                      <Input
                                        value={cal.nota || ''}
                                        onChange={(e) => updateCalibracion(criterio.codigo, 'nota', e.target.value)}
                                        placeholder="Comentario..."
                                        className="mt-1"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                  
                  {/* Notas generales */}
                  <div className="pt-4 border-t">
                    <label className="text-sm font-medium">Notas generales de calibración</label>
                    <Textarea
                      value={notasGenerales}
                      onChange={(e) => setNotasGenerales(e.target.value)}
                      placeholder="Comentarios generales sobre este ticket..."
                      className="mt-2"
                      rows={3}
                    />
                  </div>
                </div>
              </TabsContent>
              
              {/* Tab Conversación - SCROLLABLE */}
              <TabsContent value="conversacion" className="flex-1 overflow-y-auto px-6 pb-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      Hilo de Conversación
                    </CardTitle>
                    <CardDescription>
                      {data.conversation?.total_messages || 0} mensajes | 
                      Agente: {data.conversation?.agent_messages_count || 0} | 
                      Alumno: {data.conversation?.student_messages_count || 0}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {data.conversation?.all_messages?.map((msg, idx) => (
                        <ConversationMessage key={idx} message={msg} />
                      )) || (
                        <p className="text-gray-500 text-center py-8">
                          No se pudo cargar la conversación
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Tab Datos del Ticket - SCROLLABLE */}
              <TabsContent value="ticket" className="flex-1 overflow-y-auto px-6 pb-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Datos del Ticket</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">ID</p>
                        <p className="font-medium">{data.ticket?.hubspot_id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Estado</p>
                        <p className="font-medium">{data.ticket?.hs_pipeline_stage}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Tipo</p>
                        <p className="font-medium">{data.ticket?.type || '(vacío)'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Clase</p>
                        <p className="font-medium">{data.ticket?.sub_type || '(vacío)'}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm text-gray-500">Subclase</p>
                        <p className="font-medium">{data.ticket?.request || '(vacío)'}</p>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Asunto</p>
                      <p className="font-medium">{data.ticket?.subject}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Contenido inicial</p>
                      <div className="bg-gray-50 p-3 rounded-lg text-sm whitespace-pre-wrap max-h-48 overflow-auto">
                        {data.ticket?.content}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8 flex-1">No se encontraron datos</p>
        )}
        
        {/* Footer FIJO */}
        <DialogFooter className="px-6 py-4 border-t shrink-0 bg-white">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Guardar Calibración
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Componente principal
const AgentQualityCalibration = ({ token }) => {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [stats, setStats] = useState({ total: 0, pending: 0, calibrated: 0 });
  
  // Estado del batch
  const [batchStatus, setBatchStatus] = useState(null);
  // FIX CQ2 (causa raíz): usar useRef para el ID del intervalo, NO useState.
  // Con useState, la función de polling quedaba con un closure "viejo" del valor
  // de pollingInterval (siempre null, capturado antes de que se seteara) y por eso
  // el clearInterval nunca se ejecutaba de verdad: el polling seguía corriendo en loop
  // después de terminar el batch, repitiendo toasts/reloads o pisando el resultado de
  // un batch nuevo con el de uno viejo. useRef siempre lee el valor actual.
  const pollingIntervalRef = useRef(null);
  const [batchResult, setBatchResult] = useState(null); // FIX CQ2: Persistir resultado del batch
  
  // Panel de configuración
  const [instances, setInstances] = useState([]);
  const [agents, setAgents] = useState([]);
  const [config, setConfig] = useState({
    instance: 'cordoba-crea-futuro',
    limit: 15,
    agentId: '',
    dateFrom: '',
    dateTo: '',
    replaceExisting: false
  });
  
  // Filtros de vista
  const [viewFilters, setViewFilters] = useState({
    status: '',
    agentName: ''
  });
  
  // Cargar instancias disponibles
  useEffect(() => {
    const loadInstances = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/agent-quality/calibration/instances`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          // Filtrar "all" de la lista del backend (ya está hardcodeado en el select)
          const filteredInstances = (data.instances || []).filter(i => i.slug !== 'all');
          setInstances(filteredInstances);
        }
      } catch (error) {
        console.error('Error cargando instancias:', error);
      }
    };
    loadInstances();
  }, [token]);
  
  // Cargar agentes cuando cambia la instancia
  useEffect(() => {
    const loadAgents = async () => {
      try {
        const params = config.instance ? `?instance=${config.instance}` : '';
        const response = await fetch(
          `${API_URL}/api/agent-quality/calibration/agents${params}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setAgents(data.agents || []);
        }
      } catch (error) {
        console.error('Error cargando agentes:', error);
      }
    };
    loadAgents();
  }, [token, config.instance]);
  
  // Estado para rastrear la instancia del último batch generado
  const [activeInstance, setActiveInstance] = useState(null);
  
  // Cargar scores - usa activeInstance (instancia del batch) en lugar del selector global
  const loadScores = useCallback(async () => {
    // Esperar a que las instancias estén cargadas para poder mapear el nombre correcto
    if (instances.length === 0) {
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Usar activeInstance (instancia del batch generado) si existe,
      // de lo contrario usar config.instance del selector
      const instanceToFilter = activeInstance || config.instance;
      
      if (instanceToFilter && instanceToFilter !== 'all') {
        // FIX CQ4: Enviar slug directo, NO convertir a nombre
        params.append('instance', instanceToFilter);
      }
      if (viewFilters.status) params.append('status', viewFilters.status);
      if (viewFilters.agentName) params.append('agent_name', viewFilters.agentName);
      params.append('limit', '100');
      
      const response = await fetch(
        `${API_URL}/api/agent-quality/calibration/scores?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Error cargando scores');
      
      const data = await response.json();
      setScores(data.scores || []);
      setStats({
        total: data.total || 0,
        pending: data.pending || 0,
        calibrated: data.calibrated || 0
      });
      
    } catch (error) {
      toast.error('Error cargando scores');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token, config.instance, activeInstance, viewFilters, instances]);
  
  // Cargar métricas
  const loadMetrics = useCallback(async () => {
    try {
      const params = config.instance && config.instance !== 'all' ? `?instance=${config.instance}` : '';
      const response = await fetch(
        `${API_URL}/api/agent-quality/calibration/metrics${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (error) {
      console.error('Error cargando métricas:', error);
    }
  }, [token, config.instance]);
  
  // Polling del estado del batch
  const pollBatchStatus = useCallback(async (batchId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/agent-quality/calibration/batch/${batchId}/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Error obteniendo estado');
      
      const data = await response.json();
      setBatchStatus(data);
      
      // Terminar polling si batch completado, fallido, timeout, o si no hay más tickets por procesar
      const shouldStop = data.status === 'completed' || 
                         data.status === 'failed' || 
                         data.status === 'timeout' ||
                         data.status === 'partial_complete';
      
      if (shouldStop) {
        setGenerating(false);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        // FIX CQ2: Guardar resultado del batch para mostrar en UI
        setBatchResult({
          batchId: batchId,
          status: data.status,
          total: data.total,
          processed: data.processed,
          scored: data.scored,
          errors: data.errors,
          skipped: data.skipped || 0,
          completedAt: new Date().toISOString()
        });
        
        if (data.status === 'completed' || data.status === 'partial_complete') {
          // Mostrar mensaje apropiado según si se procesaron todos o no
          if (data.scored < data.total) {
            toast.success(`Batch completado: ${data.scored} tickets nuevos scoreados (${data.total - data.scored} ya existían o con errores)`);
          } else {
            toast.success(`Batch completado: ${data.scored}/${data.total} tickets scoreados`);
          }
          loadScores();
          loadMetrics();
        } else {
          toast.error(`Batch ${data.status}: ${data.errors} errores`);
        }
        
        // FIX CQ2: NO limpiar batchStatus, solo después de 30 segundos
        setTimeout(() => setBatchStatus(null), 30000);
      }
    } catch (error) {
      console.error('Error polling batch:', error);
    }
  }, [token, loadScores, loadMetrics]);
  
  // Generar batch
  const generateBatch = async () => {
    if (!config.instance) {
      toast.error('Selecciona una instancia');
      return;
    }
    
    setGenerating(true);
    setBatchStatus(null);
    
    // Establecer la instancia activa para que la lista de scores filtre por esta instancia
    setActiveInstance(config.instance);
    
    try {
      const params = new URLSearchParams({
        instance: config.instance,
        limit: config.limit.toString(),
        replace_existing: config.replaceExisting.toString()
      });
      
      if (config.agentId) params.append('agent_id', config.agentId);
      if (config.dateFrom) params.append('date_from', config.dateFrom);
      if (config.dateTo) params.append('date_to', config.dateTo);
      
      const response = await fetch(
        `${API_URL}/api/agent-quality/calibration/generate-batch?${params}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (!response.ok) {
        // FIX: mostrar el detalle real del backend (ej. "HUBSPOT_API_TOKEN no configurado")
        // en vez de un mensaje genérico, para que el usuario sepa la causa real
        const errBody = await response.json().catch(() => null);
        throw new Error(errBody?.detail || 'Error iniciando batch');
      }
      
      const data = await response.json();
      
      if (data.status === 'no_tickets') {
        toast.error(data.message);
        setGenerating(false);
        return;
      }
      
      // Manejar caso donde no hay tickets nuevos disponibles
      if (data.status === 'no_new_tickets') {
        toast.warning(data.message || 'Todos los tickets ya fueron scoreados');
        setGenerating(false);
        loadScores(); // Recargar para mostrar los existentes
        return;
      }
      
      // Mostrar el mensaje explícito del backend (cuántos se pidieron vs cuántos se encontraron)
      if (data.message) {
        if (data.new_tickets !== undefined && data.new_tickets < config.limit) {
          toast.info(data.message);
        } else {
          toast.success(data.message);
        }
      }
      
      const batchId = data.batch_id;
      setBatchStatus({ 
        batch_id: batchId, 
        status: 'processing', 
        processed: 0, 
        total: data.new_tickets || data.selected 
      });
      
      // FIX CQ2: limpiar cualquier intervalo anterior antes de arrancar uno nuevo
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      pollingIntervalRef.current = setInterval(() => pollBatchStatus(batchId), 2000);
      
    } catch (error) {
      toast.error(error.message || 'Error iniciando batch de scoring');
      console.error(error);
      setGenerating(false);
    }
  };
  
  // Eliminar score individual
  const deleteScore = async (ticketId) => {
    if (!confirm(`¿Eliminar score del ticket ${ticketId}?`)) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/agent-quality/calibration/score/${ticketId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (!response.ok) throw new Error('Error eliminando score');
      
      toast.success('Score eliminado');
      loadScores();
      loadMetrics();
    } catch (error) {
      toast.error('Error eliminando score');
      console.error(error);
    }
  };
  
  // Limpiar todos los scores
  const clearAllScores = async () => {
    const confirmMsg = config.instance && config.instance !== 'all' 
      ? `¿Eliminar TODOS los scores de ${instances.find(i => i.slug === config.instance)?.name || config.instance}?`
      : '¿Eliminar TODOS los scores de TODAS las instancias?';
    
    if (!confirm(confirmMsg)) return;
    
    try {
      // FIX CQ4: Enviar slug directo, NO convertir a nombre
      const params = config.instance && config.instance !== 'all' 
        ? `?instance=${config.instance}`
        : '';
      
      const response = await fetch(
        `${API_URL}/api/agent-quality/calibration/scores${params}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (!response.ok) throw new Error('Error eliminando scores');
      
      const data = await response.json();
      toast.success(`${data.deleted_count} scores eliminados`);
      loadScores();
      loadMetrics();
    } catch (error) {
      toast.error('Error eliminando scores');
      console.error(error);
    }
  };
  
  // Cleanup del polling al desmontar
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);
  
  // Cargar al montar
  useEffect(() => {
    loadScores();
    loadMetrics();
  }, [loadScores, loadMetrics]);
  
  return (
    <div className="space-y-6" data-testid="agent-quality-calibration">
      {/* Header con título */}
      <div className="bg-white p-4 rounded-lg border">
        <h2 className="text-2xl font-bold text-gray-900">Calibración de Calidad de Agente</h2>
        <p className="text-gray-500 mt-1">
          Valida y ajusta los scores del LLM para mejorar la precisión
        </p>
      </div>
      
      {/* Banner de piloto - permanente en TODAS las vistas del módulo (generar/historial/notas) */}
      <PilotBanner
        totalCalibrated={stats.calibrated}
        total={stats.total}
        agreementRate={metrics?.global_metrics?.agreement_rate || 0}
      />
      
      <Tabs defaultValue="generar" className="w-full">
        <TabsList>
          <TabsTrigger value="generar" data-testid="tab-generar-batch">Generar Batch</TabsTrigger>
          <TabsTrigger value="historial" data-testid="tab-historial-batches">Historial de Batches</TabsTrigger>
          <TabsTrigger value="notas" data-testid="tab-agent-notes">Nota por Agente</TabsTrigger>
        </TabsList>
        
        <TabsContent value="generar" className="space-y-6 mt-4">
      {/* Panel de Configuración */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Panel de Configuración
          </CardTitle>
          <CardDescription>
            Configura los parámetros para generar un nuevo batch de evaluación
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Fila 1: Instancia y Cantidad */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-medium text-gray-700">Instancia</label>
              <select
                value={config.instance}
                onChange={(e) => setConfig(c => ({ ...c, instance: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="config-instance-select"
              >
                <option value="all">Todas las instancias</option>
                {instances.map((inst) => (
                  <option key={inst.slug} value={inst.slug}>
                    {inst.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Cantidad de Tickets</label>
              <Input
                type="number"
                min="1"
                max="50"
                value={config.limit}
                onChange={(e) => setConfig(c => ({ ...c, limit: parseInt(e.target.value) || 15 }))}
                className="mt-1"
                data-testid="config-limit-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Filtrar por Agente</label>
              <select
                value={config.agentId}
                onChange={(e) => setConfig(c => ({ ...c, agentId: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="config-agent-select"
              >
                <option value="">Todos los agentes</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Fila 2: Fechas y Opciones */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Fecha Desde</label>
              <Input
                type="date"
                value={config.dateFrom}
                onChange={(e) => setConfig(c => ({ ...c, dateFrom: e.target.value }))}
                className="mt-1"
                data-testid="config-date-from"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Fecha Hasta</label>
              <Input
                type="date"
                value={config.dateTo}
                onChange={(e) => setConfig(c => ({ ...c, dateTo: e.target.value }))}
                className="mt-1"
                data-testid="config-date-to"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.replaceExisting}
                  onChange={(e) => setConfig(c => ({ ...c, replaceExisting: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  data-testid="config-replace-checkbox"
                />
                <span className="text-sm text-gray-700">Reemplazar scores existentes</span>
              </label>
            </div>
          </div>
          
          {/* Fila 3: Botones de acción */}
          <div className="flex flex-wrap items-center gap-3 pt-3 border-t">
            <Button 
              onClick={generateBatch} 
              disabled={generating || !config.instance}
              data-testid="generate-batch-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {generating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {batchStatus ? `Procesando ${batchStatus.processed}/${batchStatus.total}...` : 'Iniciando...'}
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Generar Batch ({config.limit} tickets)
                </>
              )}
            </Button>
            
            <div className="flex-1" />
            
            <Button 
              variant="outline" 
              onClick={clearAllScores}
              className="text-red-600 border-red-300 hover:bg-red-50"
              data-testid="clear-all-btn"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Limpiar Todos los Scores
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Barra de progreso del batch (si está generando) */}
      {batchStatus && batchStatus.status === 'processing' && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-blue-900">Generando batch...</span>
                  <span className="text-blue-700">{batchStatus.processed}/{batchStatus.total} tickets</span>
                </div>
                <div className="h-2 bg-blue-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${(batchStatus.processed / Math.max(batchStatus.total, 1)) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* FIX CQ2: Resultado del batch completado */}
      {batchResult && !generating && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${batchResult.status === 'completed' ? 'bg-green-100' : 'bg-yellow-100'}`}>
                  <CheckCircle className={`h-6 w-6 ${batchResult.status === 'completed' ? 'text-green-600' : 'text-yellow-600'}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-green-800">
                    Batch {batchResult.status === 'completed' ? 'Completado' : 'Finalizado'}
                  </h3>
                  <p className="text-sm text-green-600">
                    {new Date(batchResult.completedAt).toLocaleString('es-AR')}
                  </p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setBatchResult(null)}
                className="text-green-600 hover:text-green-800"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="grid grid-cols-4 gap-4 mt-4">
              <div className="text-center p-2 bg-white rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{batchResult.total}</p>
                <p className="text-xs text-gray-500">Solicitados</p>
              </div>
              <div className="text-center p-2 bg-white rounded-lg">
                <p className="text-2xl font-bold text-green-600">{batchResult.scored}</p>
                <p className="text-xs text-gray-500">Nuevos</p>
              </div>
              <div className="text-center p-2 bg-white rounded-lg">
                <p className="text-2xl font-bold text-yellow-600">{batchResult.skipped}</p>
                <p className="text-xs text-gray-500">Ya existían</p>
              </div>
              <div className="text-center p-2 bg-white rounded-lg">
                <p className="text-2xl font-bold text-red-600">{batchResult.errors}</p>
                <p className="text-xs text-gray-500">Errores</p>
              </div>
            </div>
            
            <div className="mt-4 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Filtrar por el batch para ver los tickets generados
                  setActiveInstance(config.instance !== 'all' ? config.instance : null);
                  loadScores();
                  setBatchResult(null);
                }}
                className="border-green-300 text-green-700 hover:bg-green-100"
              >
                <Eye className="h-4 w-4 mr-1" />
                Ver Tickets Generados
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Indicador de instancia activa */}
      {activeInstance && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm text-blue-700">
            Mostrando scores de: <strong>{instances.find(i => i.slug === activeInstance)?.name || activeInstance}</strong>
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setActiveInstance(null)}
            className="text-blue-600 hover:text-blue-800 h-6 px-2"
          >
            <XCircle className="h-4 w-4 mr-1" />
            Ver todas
          </Button>
        </div>
      )}
      
      {/* Estadísticas */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-gray-500">Total Scores</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-sm text-gray-500">Pendientes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{stats.calibrated}</p>
                <p className="text-sm text-gray-500">Calibrados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">
                  {metrics?.global_metrics?.agreement_rate || 0}%
                </p>
                <p className="text-sm text-gray-500">Acuerdo LLM-Humano</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Métricas por criterio */}
      {metrics?.criterios_metrics && Object.keys(metrics.criterios_metrics).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Métricas de Acuerdo por Criterio</CardTitle>
            <CardDescription>
              Diferencia promedio entre score LLM y calibración humana
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(metrics.criterios_metrics).map(([codigo, m]) => {
                const label = CRITERIO_LABELS[codigo] || { name: codigo, icon: '📋' };
                return (
                  <div key={codigo} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span>{label.icon}</span>
                      <span className="font-medium text-sm">{label.name}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-gray-500">Calibrados</p>
                        <p className="font-semibold">{m.total_calibrated}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Acuerdo</p>
                        <p className={`font-semibold ${m.agreement_rate >= 80 ? 'text-green-600' : 'text-yellow-600'}`}>
                          {m.agreement_rate}%
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Dif. Prom.</p>
                        <p className="font-semibold">{m.avg_diff} pts</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Dif. Máx.</p>
                        <p className="font-semibold">{m.max_diff} pts</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Filtros de vista */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm text-gray-500">Filtrar por Estado</label>
              <select
                value={viewFilters.status}
                onChange={(e) => setViewFilters(f => ({ ...f, status: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border rounded-md bg-white"
              >
                <option value="">Todos</option>
                <option value="pending">Pendientes</option>
                <option value="calibrated">Calibrados</option>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm text-gray-500">Filtrar por Agente</label>
              <Input
                value={viewFilters.agentName}
                onChange={(e) => setViewFilters(f => ({ ...f, agentName: e.target.value }))}
                placeholder="Nombre del agente..."
                className="mt-1"
              />
            </div>
            <Button variant="outline" onClick={loadScores} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar lista
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Tabla de scores */}
      <Card>
        <CardHeader>
          <CardTitle>Tickets Scoreados</CardTitle>
          <CardDescription>
            Click en un ticket para ver el detalle y calibrar
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : scores.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 text-lg">No hay scores generados</p>
              <p className="text-sm text-gray-400 mt-1 mb-6">Usa el botón &quot;Generar Batch&quot; de arriba para comenzar</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ticket ID</TableHead>
                  <TableHead>Agente</TableHead>
                  <TableHead className="text-center">Score LLM</TableHead>
                  <TableHead className="text-center">Score Calibrado</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-center">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scores.map((score) => (
                  <TableRow 
                    key={score.ticket_id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setSelectedTicket(score.ticket_id)}
                  >
                    <TableCell className="font-mono text-sm">
                      <TicketLink ticketId={score.ticket_id} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        {score.agent_name || 'N/A'}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <ScoreBadge score={score.score_global} />
                        <ProvisionalBadge
                          agreementRate={metrics?.global_metrics?.agreement_rate || 0}
                          totalCalibrated={stats.calibrated}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {score.score_global_calibrado !== undefined ? (
                        <ScoreBadge score={score.score_global_calibrado} />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge 
                        variant={score.calibration_status === 'calibrated' ? 'default' : 'secondary'}
                        className="gap-1"
                      >
                        {score.calibration_status === 'calibrated' ? (
                          <><CheckCheck className="h-3 w-3" /> Calibrado</>
                        ) : (
                          <><Clock className="h-3 w-3" /> Pendiente</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {score.generated_at ? 
                        new Date(score.generated_at).toLocaleDateString('es-AR') : 
                        '-'
                      }
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTicket(score.ticket_id);
                          }}
                          title="Ver detalle"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteScore(score.ticket_id);
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Eliminar score"
                          data-testid={`delete-score-${score.ticket_id}`}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </TabsContent>
        
        <TabsContent value="historial" className="mt-4">
          <BatchHistory
            token={token}
            instances={instances}
            agreementRate={metrics?.global_metrics?.agreement_rate || 0}
            totalCalibrated={stats.calibrated}
          />
        </TabsContent>
        
        <TabsContent value="notas" className="mt-4">
          <AgentNotes
            token={token}
            instances={instances}
            agreementRate={metrics?.global_metrics?.agreement_rate || 0}
            totalCalibrated={stats.calibrated}
          />
        </TabsContent>
      </Tabs>
      
      {/* Modal de detalle */}
      <CalibrationDetailModal
        ticketId={selectedTicket}
        isOpen={!!selectedTicket}
        onClose={() => setSelectedTicket(null)}
        token={token}
        agreementRate={metrics?.global_metrics?.agreement_rate || 0}
        totalCalibrated={stats.calibrated}
        totalScores={stats.total}
        onCalibrated={() => {
          loadScores();
          loadMetrics();
        }}
      />
    </div>
  );
};

export default AgentQualityCalibration;
